import { CustomerTypeEnum } from 'common/enums/CustomerTypeEnum'
import { PaymentFormStepTypeEnum } from 'common/enums/PaymentFormStepTypeEnum'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import { ProductInterface } from 'common/types/entities/ProductInterface'
import { OfferInterface, OneShotPlan } from 'common/types/OfferInterface'
import {
  cashOnDeliveryInstructionPlaceholder,
  customerTypePlaceholder,
} from 'publisher/reducers/paymentReducer'
import { PaymentState } from 'publisher/store/payment/PaymentStateInterface'

const deadlineTimestampPlaceholder = '%DEADLINE_TIMESTAMP%'
const deadlineExpirationUrlPlaceholder = '%DEADLINE_EXPIRATION_URL%'

export function getDlocalDocumentNumber(paymentState: PaymentState) {
  return paymentState.dlocalDocumentNumber
}

export function getMercadoPagoPublicKey(paymentState: PaymentState) {
  return paymentState.mercadoPagoPublicKey
}

export function getPurchaseProcessId(paymentState: PaymentState) {
  return paymentState.purchaseProcessId
}

export function getPaymentMethods(paymentState: PaymentState) {
  try {
    return JSON.parse(paymentState.paymentMethods) as PaymentMethodEnum[]
  } catch (_e) {
    return []
  }
}

function getFirstPaymentMethod(paymentState: PaymentState) {
  const paymentMethods = getPaymentMethods(paymentState)

  if (paymentMethods.length > 0) {
    return paymentMethods[0]
  }

  return null
}

export function getActivePaymentMethod(paymentState: PaymentState) {
  return (
    paymentState.customerPaymentMethod || getFirstPaymentMethod(paymentState)
  )
}

export const getActiveStripePaymentMethod = (paymentState: PaymentState) => {
  return paymentState.stripePaymentMethod
}

export function getActivePricePlan(state: PaymentState) {
  const checkedPlanId = getCheckedPricePlanId(state)
  try {
    const offer: OfferInterface = JSON.parse(state.offer)
    if (!state.checkedPlanId) {
      return offer.pricePlans[0]
    }

    return offer.pricePlans.find(plan => plan.id === checkedPlanId)
  } catch (_e) {
    return null
  }
}

export function getPricePlans(state: PaymentState) {
  try {
    const offer: OfferInterface = JSON.parse(state.offer)
    if (!state.checkedPlanId) {
      return []
    }

    return offer.pricePlans
  } catch (_e) {
    return []
  }
}

export function getCheckedBump(state: PaymentState) {
  try {
    const bump: OfferInterface<OneShotPlan> = JSON.parse(state.offerBump)

    if (!bump) {
      return
    }
    const isChecked = isOrderBumpChecked(state)

    if (!isChecked) {
      return
    }

    return bump
  } catch (_e) {
    return
  }
}

export function getOrderBumpId(state: PaymentState) {
  try {
    const bump: OfferInterface<OneShotPlan> = JSON.parse(state.offerBump)

    if (!bump) {
      return null
    }

    return bump.id
  } catch (_e) {
    return
  }
}

export function getCheckedPricePlan(state: PaymentState) {
  return getPricePlans(state).find(
    plan => plan.id === Number(state.checkedPlanId),
  )
}

export function getCheckedPricePlanId(state: PaymentState) {
  return Number(state.checkedPlanId)
}

export function getCouponCode(state: PaymentState) {
  return state.couponCode
}

export function getCheckedCoupon(state: PaymentState) {
  return state.checkedCoupon
}

export function getCouponError(state: PaymentState) {
  return state.couponError
}

export function getCheckedBumpId(state: PaymentState) {
  return state.checkedBumpId
}

export function isOrderBumpChecked(state: PaymentState) {
  // fallback can be removed after migration
  return state.isOrderBumpChecked || false
}

function getCheckedBumpIds(state: PaymentState) {
  const bump = getCheckedBump(state)

  return bump ? [bump.id] : []
}

export function isOnlyPersonalAvailable(state: PaymentState) {
  return state.isOnlyPersonalAvailable
}

export function getOfferOwnerCountry(state: PaymentState) {
  return state.offerOwnerCountry
}

export function isCustomerPersonal(state: PaymentState) {
  return state.customerType === CustomerTypeEnum.Personal
}

export function getCustomerType(state: PaymentState) {
  return state.customerType === customerTypePlaceholder
    ? null
    : (state.customerType as CustomerTypeEnum)
}

export function getProduct(state: PaymentState) {
  try {
    return JSON.parse(state.product) as ProductInterface
  } catch (_e) {
    return null
  }
}

export function getDigitalProduct(state: PaymentState) {
  try {
    return state.digitalProduct
  } catch (_e) {
    return null
  }
}

export function getProductQuantity(state: PaymentState) {
  return state.productQuantity
}

export function getProductActiveVariant(state: PaymentState) {
  return state.productActiveVariant
}

export function getSelectedProductPrice(state: PaymentState) {
  const activeVariant = getProductActiveVariant(state)
  const product = getProduct(state)

  return activeVariant?.price
    ? activeVariant?.price
    : product?.id
      ? product.price
      : 0
}

export function getTwoStepPaymentFormStepType(state: PaymentState) {
  return state.twoStepPaymentFormStepType
}

export function isTwoStepFormPaymentStep(state: PaymentState) {
  return (
    state.twoStepPaymentFormStepType === PaymentFormStepTypeEnum.STEP_PAYMENT
  )
}

export const getDeadlineTime = ({ deadlineTime }: PaymentState) => {
  if (deadlineTime === deadlineTimestampPlaceholder) {
    return null
  }

  return Number.parseInt(deadlineTime) * 1000
}

export const getDeadlineExpirationUrl = ({
  deadlineExpirationUrl,
}: PaymentState) => {
  if (deadlineExpirationUrl === deadlineExpirationUrlPlaceholder) {
    return null
  }

  return deadlineExpirationUrl
}

export function getCashOnDeliveryInstruction(state: PaymentState) {
  return state.cashOnDeliveryInstruction ===
    cashOnDeliveryInstructionPlaceholder
    ? null
    : state.cashOnDeliveryInstruction
}

export function getXenditPublicKey(paymentState: PaymentState) {
  return paymentState.xenditPublicKey
}

export function getCustomerEmail(paymentState: PaymentState) {
  return paymentState.customerEmail
}

export function getSelectedShipping(paymentState: PaymentState) {
  return paymentState.shipping
}

export const getOfferId = (paymentState: PaymentState) => {
  try {
    return JSON.parse(paymentState.offer).id
  } catch (_err) {
    return null
  }
}

export const getOfferPricing = (paymentState: PaymentState) =>
  paymentState.offerPricing

export const getOfferCurrency = (paymentState: PaymentState) => {
  const product = getProduct(paymentState)
  const pricePlan = getActivePricePlan(paymentState)
  return product?.currency ?? pricePlan?.currency ?? 'usd'
}

export const getStripeAccountId = (paymentState: PaymentState) =>
  paymentState.stripeAccountId

const paymentSelectors = {
  getDlocalDocumentNumber,
  getPurchaseProcessId,
  getPaymentMethods,
  getActivePricePlan,
  getCheckedPricePlan,
  getCheckedPricePlanId,
  getCouponCode,
  getPricePlans,
  getCheckedCoupon,
  getCheckedBumpIds,
  getActivePaymentMethod,
  isCustomerPersonal,
  getProduct,
  getProductQuantity,
  getProductActiveVariant,
  getTwoStepPaymentFormStepType,
  getCashOnDeliveryInstruction,
  getDeadlineTime,
  getDeadlineExpirationUrl,
  getSelectedShipping,
  getOfferId,
  getOfferPricing,
  getStripeAccountId,
  isTwoStepFormPaymentStep,
}

export default paymentSelectors
